import React, { useEffect, useMemo, useState } from "react";
import {
  collection, getDocs, setDoc, updateDoc, deleteDoc, doc, writeBatch, serverTimestamp, orderBy, query
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

/**
 * Drop-in page to manage:
 *  - return_reasons: { code, description, requiresDefect, deprecated }
 *  - defect_codes:  { code, description, deprecated }
 *
 * Features:
 *  - List, search, filter, sort
 *  - Add/edit inline
 *  - Toggle deprecated
 *  - Delete
 *  - Bulk import (paste or CSV upload; also accepts JSON array)
 *  - Export CSV
 */

const TABS = [
  { key: "return_reasons", label: "Reason Codes", hasRequires: true },
  { key: "defect_codes", label: "Defect Codes", hasRequires: false },
];

export default function CodesManager() {
  const [tab, setTab] = useState(TABS[0]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showDeprecated, setShowDeprecated] = useState(true);
  const [sortBy, setSortBy] = useState("code"); // 'code' | 'description' | 'deprecated'
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'

  // new/edit form state
  const [draft, setDraft] = useState({ code: "", description: "", requiresDefect: false, deprecated: false });
  const [editCode, setEditCode] = useState(null); // code being edited (doc id)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const coll = useMemo(() => collection(db, tab.key), [tab.key]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        // Optional orderBy for stable lists
        const qy = query(coll, /* orderBy("deprecated","asc"), */ /* orderBy("code","asc") */);
        const snap = await getDocs(qy);
        if (!isMounted) return;
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRows(data);
      } catch (e) {
        setError("Failed to load codes.");
        console.error(e);
      } finally {
        setLoading(false);
        setEditCode(null);
        setDraft({ code: "", description: "", requiresDefect: false, deprecated: false });
      }
    })();
    return () => { isMounted = false; };
  }, [coll]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = rows.filter(r => showDeprecated ? true : !r.deprecated);
    if (term) {
      list = list.filter(r =>
        (r.code || "").toLowerCase().includes(term) ||
        (r.description || "").toLowerCase().includes(term)
      );
    }
    const cmp = (a, b, k) => {
      const va = (a[k] ?? "").toString().toLowerCase();
      const vb = (b[k] ?? "").toString().toLowerCase();
      if (va < vb) return -1;
      if (va > vb) return 1;
      return 0;
    };
    list.sort((a, b) => {
      let c = 0;
      if (sortBy === "deprecated") c = (a.deprecated === b.deprecated) ? 0 : (a.deprecated ? 1 : -1);
      else c = cmp(a, b, sortBy);
      return sortDir === "asc" ? c : -c;
    });
    return list;
  }, [rows, q, showDeprecated, sortBy, sortDir]);

  const resetDraft = () => setDraft({ code: "", description: "", requiresDefect: false, deprecated: false });

  const upsert = async () => {
    setBusy(true);
    setError("");
    try {
      const code = (draft.code || "").trim().toUpperCase();
      if (!code) throw new Error("Code required.");
      if (!draft.description?.trim()) throw new Error("Description required.");
      const docRef = doc(db, tab.key, code);
      const payload = {
        code,
        description: draft.description.trim(),
        deprecated: !!draft.deprecated,
        updatedAt: serverTimestamp(),
      };
      if (tab.hasRequires) payload.requiresDefect = !!draft.requiresDefect;

      // setDoc with merge false ensures canonical fields; set createdAt once via merge true
      await setDoc(docRef, { createdAt: serverTimestamp(), ...payload }, { merge: true });

      // update local list
      setRows(prev => {
        const idx = prev.findIndex(r => r.code === code);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...payload, id: code };
          return next;
        }
        return [{ id: code, ...payload }, ...prev];
      });

      resetDraft();
      setEditCode(null);
    } catch (e) {
      setError(e.message || "Save failed.");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const beginEdit = (r) => {
    setEditCode(r.code);
    setDraft({
      code: r.code,
      description: r.description || "",
      requiresDefect: !!r.requiresDefect,
      deprecated: !!r.deprecated,
    });
  };

  const cancelEdit = () => { setEditCode(null); resetDraft(); };

  const toggleDeprecated = async (r) => {
    setBusy(true);
    setError("");
    try {
      await updateDoc(doc(db, tab.key, r.code), { deprecated: !r.deprecated, updatedAt: serverTimestamp() });
      setRows(prev => prev.map(x => x.code === r.code ? { ...x, deprecated: !x.deprecated } : x));
    } catch (e) {
      setError("Failed to toggle deprecated.");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete code ${r.code}?`)) return;
    setBusy(true);
    setError("");
    try {
      await deleteDoc(doc(db, tab.key, r.code));
      setRows(prev => prev.filter(x => x.code !== r.code));
    } catch (e) {
      setError("Delete failed.");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  // Bulk import: accept JSON array OR CSV with headers:
  // Reasons: code,description,requiresDefect,deprecated
  // Defects: code,description,deprecated
  const parseBulk = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return [];
    // Try JSON first
    try {
      const json = JSON.parse(trimmed);
      if (Array.isArray(json)) return json;
    } catch (_) { /* not JSON */ }

    // CSV (very simple): supports commas inside quotes; trims whitespace.
    return csvToObjects(trimmed);
  };

  const bulkImport = async () => {
    setBusy(true);
    setError("");
    try {
      const items = parseBulk(bulkText);
      if (!Array.isArray(items) || items.length === 0) throw new Error("Nothing to import.");
      const batch = writeBatch(db);
      const normalized = [];
      for (const raw of items) {
        let code = (raw.code || "").toString().trim().toUpperCase();
        let description = (raw.description || "").toString().trim();
        if (!code || !description) continue;

        const docRef = doc(db, tab.key, code);
        const payload = {
          code,
          description,
          deprecated: strToBool(raw.deprecated, false),
          updatedAt: serverTimestamp(),
        };
        if (tab.hasRequires) payload.requiresDefect = strToBool(raw.requiresDefect, false);

        // use merge true to set createdAt only if missing
        batch.set(docRef, { createdAt: serverTimestamp(), ...payload }, { merge: true });
        normalized.push({ id: code, ...payload });
      }
      if (normalized.length === 0) throw new Error("No valid rows found (need at least code & description).");
      await batch.commit();

      // refresh UI: merge normalized into rows
      setRows(prev => {
        const map = new Map(prev.map(r => [r.code, r]));
        normalized.forEach(n => map.set(n.code, { ...(map.get(n.code) || {}), ...n }));
        return Array.from(map.values());
      });
      setBulkOpen(false);
      setBulkText("");
    } catch (e) {
      setError(e.message || "Bulk import failed.");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const exportCSV = () => {
    const cols = tab.hasRequires
      ? ["code","description","requiresDefect","deprecated"]
      : ["code","description","deprecated"];
    const header = cols.join(",");
    const lines = filtered.map(r =>
      cols.map(c => csvEscape(r[c])).join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tab.key}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map(t => (
          <button key={t.key}
            onClick={() => { setTab(t); setQ(""); setEditCode(null); resetDraft(); }}
            className={`px-3 py-2 rounded-md border ${tab.key === t.key ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"}`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <input
            className="border rounded-md px-3 py-2 text-sm"
            placeholder="Search code or description…"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showDeprecated} onChange={(e)=>setShowDeprecated(e.target.checked)} />
            Show deprecated
          </label>
          <button onClick={() => setBulkOpen(true)} className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">Bulk import</button>
          <button onClick={exportCSV} className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">Export CSV</button>
        </div>
      </div>

      {/* Add / Edit form */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          <Field
            label="Code"
            value={draft.code}
            onChange={v => setDraft(d => ({ ...d, code: v.toUpperCase() }))}
            placeholder="SCMD"
            disabled={!!editCode} // cannot change id when editing
          />
          <Field
            label="Description"
            value={draft.description}
            onChange={v => setDraft(d => ({ ...d, description: v }))}
            placeholder="Manufacturing defect"
          />
          {tab.hasRequires && (
            <Check label="Requires defect" checked={!!draft.requiresDefect} onChange={v => setDraft(d => ({ ...d, requiresDefect: v }))} />
          )}
          <Check label="Deprecated" checked={!!draft.deprecated} onChange={v => setDraft(d => ({ ...d, deprecated: v }))} />
        </div>
        <div className="mt-3 flex gap-2">
          {editCode ? (
            <>
              <button disabled={busy} onClick={upsert} className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700">
                {busy ? "Saving…" : "Save"}
              </button>
              <button disabled={busy} onClick={cancelEdit} className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50">Cancel</button>
            </>
          ) : (
            <button disabled={busy} onClick={upsert} className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700">
              {busy ? "Adding…" : "Add code"}
            </button>
          )}
          {error && <span className="ml-2 text-sm text-red-600">{error}</span>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-left">
              <Th label="Code" sortBy="code" setSortBy={setSortBy} sortByState={sortBy} sortDir={sortDir} setSortDir={setSortDir}/>
              <Th label="Description" sortBy="description" setSortBy={setSortBy} sortByState={sortBy} sortDir={sortDir} setSortDir={setSortDir}/>
              {tab.hasRequires && <th className="px-3 py-2">Requires Defect</th>}
              <Th label="Deprecated" sortBy="deprecated" setSortBy={setSortBy} sortByState={sortBy} sortDir={sortDir} setSortDir={setSortDir}/>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td className="px-3 py-3 text-gray-500" colSpan={tab.hasRequires ? 5 : 4}>Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td className="px-3 py-3 text-gray-500" colSpan={tab.hasRequires ? 5 : 4}>No codes.</td></tr>
            )}
            {!loading && filtered.map(r => (
              <tr key={r.code} className="border-t">
                <td className="px-3 py-2 font-mono">{r.code}</td>
                <td className="px-3 py-2">{r.description}</td>
                {tab.hasRequires && (
                  <td className="px-3 py-2">{r.requiresDefect ? "Yes" : "No"}</td>
                )}
                <td className="px-3 py-2">{r.deprecated ? <span className="text-red-600">Yes</span> : "No"}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    <button className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50" onClick={() => beginEdit(r)}>Edit</button>
                    <button className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50" onClick={() => toggleDeprecated(r)}>
                      {r.deprecated ? "Undeprecate" : "Deprecate"}
                    </button>
                    <button className="px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50" onClick={() => remove(r)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk import drawer */}
      {bulkOpen && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/30" onClick={() => setBulkOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[460px] bg-white shadow-2xl p-4 overflow-y-auto">
            <div className="flex items-center mb-3">
              <h3 className="text-lg font-semibold">Bulk import — {tab.label}</h3>
              <button onClick={() => setBulkOpen(false)} className="ml-auto px-2 py-1 border rounded-md">Close</button>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              Paste <b>JSON array</b> or <b>CSV</b>. Existing codes are upserted (doc ID = code).
            </p>

            <div className="text-xs bg-gray-50 border rounded p-2 mb-3">
              <div className="font-medium mb-1">CSV headers:</div>
              {tab.hasRequires
                ? <code>code,description,requiresDefect,deprecated</code>
                : <code>code,description,deprecated</code>}
            </div>

            <textarea
              value={bulkText}
              onChange={(e)=>setBulkText(e.target.value)}
              placeholder='Example JSON: [{"code":"SCMD","description":"Manufacturing defect","requiresDefect":true}]'
              rows={12}
              className="w-full border rounded p-2 mb-3"
            />

            <FileDrop onParsed={(txt) => setBulkText(txt)} />

            <div className="flex gap-2 mt-2">
              <button disabled={busy} onClick={bulkImport} className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700">
                {busy ? "Importing…" : "Import"}
              </button>
              <button onClick={() => setBulkText("")} className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50">Clear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- little components ---------- */

function Field({ label, value, onChange, placeholder = "", disabled = false }) {
  return (
    <div>
      <label className="block text-sm text-gray-700 mb-1">{label}</label>
      <input
        disabled={disabled}
        className={`w-full rounded-md border px-3 py-2 outline-none ${disabled ? "bg-gray-50" : "focus:ring-2 focus:ring-indigo-200"} border-gray-300`}
        value={value}
        onChange={(e)=>onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e)=>onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Th({ label, sortBy, setSortBy, sortByState, sortDir, setSortDir }) {
  const active = sortByState === sortBy;
  const nextDir = active && sortDir === "asc" ? "desc" : "asc";
  return (
    <th
      className="px-3 py-2 cursor-pointer select-none"
      onClick={() => { setSortBy(sortBy); setSortDir(nextDir); }}
      title="Sort"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (sortDir === "asc" ? "▲" : "▼") : ""}
      </span>
    </th>
  );
}

/* ---------- helpers ---------- */

function csvEscape(v) {
  if (v === undefined || v === null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function strToBool(v, def=false) {
  if (typeof v === "boolean") return v;
  if (v == null) return def;
  const s = String(v).trim().toLowerCase();
  return ["1","true","yes","y"].includes(s);
}

/** Simple CSV -> objects parser. Handles quoted fields and commas in quotes. */
function csvToObjects(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  if (lines.length === 0) return [];
  const header = parseCsvLine(lines[0]).map(h => h.trim());
  const out = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const obj = {};
    header.forEach((h, idx) => obj[h] = cols[idx]);
    out.push(obj);
  }
  return out;
}
function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i=0; i<line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"') {
        if (line[i+1] === '"') { cur += '"'; i++; } // escaped quote
        else inQ = false;
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ',') { out.push(cur); cur = ""; }
      else cur += ch;
    }
  }
  out.push(cur);
  return out.map(s => s.trim());
}

/** CSV/JSON file dropper */
function FileDrop({ onParsed }) {
  const [name, setName] = useState("");
  return (
    <div
      className="mt-2 p-3 border border-dashed rounded-md text-sm text-gray-600"
      onDragOver={(e)=>{ e.preventDefault(); }}
      onDrop={(e)=>{
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (!f) return;
        setName(f.name);
        const reader = new FileReader();
        reader.onload = () => onParsed(String(reader.result || ""));
        reader.readAsText(f);
      }}
    >
      <div className="flex items-center gap-2">
        <span>Drag & drop CSV/JSON here</span>
        {name && <span className="text-gray-800">· {name}</span>}
        <span className="ml-auto text-xs text-gray-400">(or paste above)</span>
      </div>
    </div>
  );
}
